type FragmentListLike = {
  getMaterial?: (fragId: number) => unknown;
  modelId?: number;
};

const CRASH_SAFE_FLAG = "__avvirCrashSafeGetMaterial";
const loggedKeys = new Set<string>();
const MAX_LOGGED = 20;

/**
 * Wrap a FragmentList prototype's `getMaterial` so it can never throw.
 *
 * Under Autodesk's large-model experience (fragment consolidation / on-demand geometry) a fragId can
 * remain enumerable on the instance tree while its vizmesh is not resident. The stock
 * `getMaterial(fragId)` then dereferences an undefined mesh and throws
 * `Cannot read properties of undefined (reading 'material')` — and because it happens inside the
 * per-frame render loop (applyVisibility → sortByMaterial → getMaterialId → getMaterial) it throws
 * every animation frame and the viewer appears frozen (HMA-1911).
 *
 * Returning `undefined` instead lets the caller (`getMaterialId`) fall back to its default-material
 * path, so the frame renders (that fragment simply isn't drawn) rather than wedging the loop.
 *
 * Exported separately from {@link patchFragmentListGetMaterial} so it can be unit tested against a
 * plain fake without the real Autodesk global.
 *
 * @returns true if it wrapped the method, false if there was nothing to wrap or it was already wrapped.
 */
export const makeGetMaterialCrashSafe = (fragmentListProto: FragmentListLike | undefined): boolean => {
  if (!fragmentListProto || typeof fragmentListProto.getMaterial !== "function") {
    return false;
  }
  if ((fragmentListProto.getMaterial as { [CRASH_SAFE_FLAG]?: boolean })[CRASH_SAFE_FLAG]) {
    return false;
  }

  const originalGetMaterial = fragmentListProto.getMaterial;
  function crashSafeGetMaterial(this: FragmentListLike, fragId: number) {
    try {
      return originalGetMaterial.call(this, fragId);
    } catch (e) {
      const key = `${this?.modelId}:${fragId}`;
      if (loggedKeys.size < MAX_LOGGED && !loggedKeys.has(key)) {
        loggedKeys.add(key);
        console.warn(`Skipped unresolvable material for model ${this?.modelId} fragment ${fragId} (HMA-1911)`, e);
      }
      return undefined;
    }
  }
  (crashSafeGetMaterial as { [CRASH_SAFE_FLAG]?: boolean })[CRASH_SAFE_FLAG] = true;
  fragmentListProto.getMaterial = crashSafeGetMaterial;
  return true;
};

/**
 * Apply {@link makeGetMaterialCrashSafe} to the live Autodesk FragmentList prototype. Idempotent and
 * safe to call before every viewer initialization. No-ops if the viewer library isn't loaded yet
 * (e.g. in unit tests, where the fake exposes no prototype).
 */
export const patchFragmentListGetMaterial = (): boolean => {
  const fragmentListProto = (Autodesk as unknown as {
    Viewing?: { Private?: { FragmentList?: { prototype?: FragmentListLike } } };
  })?.Viewing?.Private?.FragmentList?.prototype;
  return makeGetMaterialCrashSafe(fragmentListProto);
};
